import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Save, AlertCircle, ExternalLink, Trash2, KeyRound } from "lucide-react";
import { adminGetMetaPixel, adminPutMetaPixel, adminDeleteMetaPixel } from "../../lib/api";

/**
 * Admin → Meta Pixel (Facebook + Instagram conversions). iter47t.
 *
 * Paste your 15-16 digit Pixel ID once and `<MetaPixelLoader />` injects
 * the official fbq() snippet on every public page, plus we fire:
 *   - ViewContent       on PDP mount
 *   - AddToCart         on cart add (ReadyMadeProduct + Customizer)
 *   - InitiateCheckout  on cart "Checkout" click
 *   - Purchase          on successful order
 *
 * Pixel ID is NOT secret (it ships in the page HTML to anyone who views
 * source), so no need to mask it. Saved in MongoDB site_settings so the
 * operator can rotate it any time with no redeploy.
 */
export default function MetaPixelSettingsTab({ pw }) {
  const [pixelId, setPixelId] = useState("");
  const [savedId, setSavedId] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  // iter47u CAPI fields
  const [capiToken, setCapiToken] = useState("");
  const [capiHasToken, setCapiHasToken] = useState(false);
  const [capiHint, setCapiHint] = useState("");
  const [testCode, setTestCode] = useState("");

  useEffect(() => {
    adminGetMetaPixel(pw).then((r) => {
      setPixelId(r.pixel_id || "");
      setSavedId(r.pixel_id || "");
      setUpdatedAt(r.updated_at);
      setCapiHasToken(Boolean(r.capi_token_set));
      setCapiHint(r.capi_token_hint || "");
      setTestCode(r.test_event_code || "");
    }).catch(() => {});
  }, [pw]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      // Build payload that only sends the fields the user actually touched
      // — preserves existing values when one half is left blank.
      const body = {};
      if (pixelId.trim()) body.pixel_id = pixelId.trim();
      if (capiToken.trim()) body.capi_token = capiToken.trim();
      // Always send test_event_code (so clearing it works).
      body.test_event_code = testCode.trim();
      if (!body.pixel_id && !body.capi_token && body.test_event_code === "") {
        toast.error("Enter a Pixel ID or CAPI token to save.");
        setSaving(false);
        return;
      }
      const r = await adminPutMetaPixel(pw, body);
      setSavedId(r.pixel_id || savedId);
      setCapiHasToken(Boolean(r.capi_token_set));
      setTestCode(r.test_event_code || "");
      // Clear the CAPI input — token shouldn't sit in the form field
      // after saving (defence against shoulder-surfing).
      if (body.capi_token) setCapiToken("");
      toast.success("Meta integration saved — refresh any public page to start tracking.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Disable Meta Pixel? Tracking will stop on the next page reload until you re-enter a Pixel ID.")) return;
    try {
      await adminDeleteMetaPixel(pw);
      setPixelId(""); setSavedId("");
      toast.success("Meta Pixel disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable");
    }
  };

  return (
    <div className="space-y-6" data-testid="meta-pixel-settings-tab">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <Activity size={22} className="text-[var(--tc-red)]" /> Meta Pixel (Facebook + Instagram)
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Tracks ViewContent, AddToCart, InitiateCheckout, and Purchase events. Required for Instagram product tagging, Meta Advantage+ catalog ads, and cart-abandonment retargeting.
        </p>
      </div>

      {/* Status */}
      <div className={`border p-4 ${savedId ? "border-emerald-700 bg-emerald-950/30" : "border-amber-700 bg-amber-950/30"}`}>
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className={savedId ? "text-emerald-400 mt-0.5" : "text-amber-400 mt-0.5"} />
          <div className="flex-1">
            <div className={`font-mono text-xs tracking-widest ${savedId ? "text-emerald-200" : "text-amber-200"}`}>
              STATUS: {savedId ? "META PIXEL IS LIVE" : "NOT CONFIGURED YET"}
            </div>
            {savedId ? (
              <div className="text-xs text-zinc-400 mt-1">
                Pixel ID <code className="text-zinc-200" data-testid="meta-pixel-saved-id">{savedId}</code>
                {updatedAt && <> · last saved {new Date(updatedAt).toLocaleString()}</>}
              </div>
            ) : (
              <div className="text-xs text-zinc-400 mt-1">No Pixel ID yet — paste it below to activate tracking.</div>
            )}
          </div>
        </div>
      </div>

      {/* Editor */}
      <form onSubmit={handleSave} className="space-y-4 border border-[#222] bg-[#0a0a0a] p-5">
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">
            META PIXEL ID (15 OR 16 DIGITS) — public, ships in page HTML
          </span>
          <input
            type="text"
            value={pixelId}
            onChange={(e) => setPixelId(e.target.value)}
            data-testid="meta-pixel-id-input"
            placeholder="e.g. 1234567890123456"
            inputMode="numeric"
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
        </label>

        {/* iter47u — Conversions API (server-side fallback) */}
        <label className="block pt-2 border-t border-[#222]">
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">
            CAPI ACCESS TOKEN — server-side secret, never shown in HTML
            {capiHasToken && (
              <span className="ml-2 text-emerald-400">
                · ACTIVE {capiHint && <code className="text-zinc-500">({capiHint})</code>}
              </span>
            )}
          </span>
          <input
            type="password"
            value={capiToken}
            onChange={(e) => setCapiToken(e.target.value)}
            data-testid="meta-capi-token-input"
            placeholder={capiHasToken ? "Leave blank to keep current token" : "Paste long access token from Events Manager → CAPI"}
            autoComplete="new-password"
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
          <p className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">
            Restores ~40% of Purchase events lost to iOS 17+ tracking blockers.
            Generate at Events Manager → Settings → Conversions API → Generate Access Token.
            The token is sent server→server only, never in browser HTML.
          </p>
        </label>

        <label className="block">
          <span className="font-mono text-[11px] tracking-widest text-zinc-400">TEST EVENT CODE (optional)</span>
          <input
            type="text"
            value={testCode}
            onChange={(e) => setTestCode(e.target.value)}
            data-testid="meta-test-event-code-input"
            placeholder="e.g. TEST12345 — from Events Manager → Test Events"
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
          <p className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">
            Paste the code from Events Manager → Test Events to route server events into the Test Events tab for verification. <span className="text-amber-400">Clear it after testing</span> so live traffic isn't flagged as test data.
          </p>
        </label>

        <div className="flex gap-2 pt-1">
          <button
            type="submit" disabled={saving}
            data-testid="meta-pixel-save-btn"
            className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? "SAVING…" : "SAVE META SETTINGS"}
          </button>
          {savedId && (
            <button
              type="button" onClick={handleDelete}
              data-testid="meta-pixel-delete-btn"
              className="border border-red-900 hover:bg-red-950 text-red-300 px-4 py-2 font-mono text-xs tracking-widest flex items-center gap-2"
            >
              <Trash2 size={14} /> DISABLE PIXEL
            </button>
          )}
        </div>
      </form>

      {/* iter47u — CAPI token clickable walkthrough */}
      <div className="border border-sky-900 bg-sky-950/20 p-5 space-y-4" data-testid="meta-capi-walkthrough">
        <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
          <KeyRound size={18} className="text-sky-400" /> Get your CAPI Access Token (5 min)
        </h3>
        <p className="text-sm text-zinc-300">
          The Conversions API sends purchases &amp; leads to Meta <em>server-to-server</em>, recovering the
          ~40% of events lost to iOS/ad-blockers. Follow these steps — each link opens in a new tab.
        </p>
        <ol className="list-decimal pl-5 space-y-3 text-sm text-zinc-300">
          <li>
            Open{" "}
            <a
              href="https://business.facebook.com/events_manager2/list/dataset/1032036405959307/settings"
              target="_blank" rel="noreferrer"
              data-testid="capi-link-settings"
              className="text-sky-400 hover:underline inline-flex items-center gap-1"
            >
              Events Manager → your Pixel → Settings <ExternalLink size={12} />
            </a>{" "}
            (opens directly to pixel <code className="text-zinc-200">1032036405959307</code>).
          </li>
          <li>
            Scroll to the <strong>Conversions API</strong> section → click{" "}
            <strong className="text-zinc-100">&quot;Generate access token&quot;</strong> (under
            &quot;Set up manually / Implementation&quot;). Confirm any permission prompt.
          </li>
          <li>
            Copy the long token it shows (starts with <code className="text-zinc-200">EAA…</code>) and{" "}
            <strong className="text-zinc-100">paste it into the &quot;CAPI ACCESS TOKEN&quot; field above</strong>, then hit
            SAVE META SETTINGS. The token is stored server-side only — it never appears in your page HTML.
          </li>
          <li>
            <strong>Test it:</strong> open{" "}
            <a
              href="https://business.facebook.com/events_manager2/list/dataset/1032036405959307/test_events"
              target="_blank" rel="noreferrer"
              data-testid="capi-link-testevents"
              className="text-sky-400 hover:underline inline-flex items-center gap-1"
            >
              Events Manager → Test Events <ExternalLink size={12} />
            </a>, copy the <code className="text-zinc-200">TEST…</code> code shown there, paste it into the
            <strong className="text-zinc-100"> TEST EVENT CODE</strong> field above and SAVE.
          </li>
          <li>
            Browse your live site (PageView, open a product, add to cart). Within a few seconds you&apos;ll see
            <strong className="text-zinc-100"> server</strong> events appear in that Test Events tab, deduplicated against
            the browser pixel. Counts in this dashboard turn non-zero.
          </li>
          <li>
            <span className="text-amber-400 font-semibold">Important:</span> once verified,
            <strong className="text-zinc-100"> clear the TEST EVENT CODE</strong> and SAVE again — otherwise real
            traffic keeps getting flagged as test data and won&apos;t optimise your ads.
          </li>
        </ol>
      </div>


      {/* Catalog feed */}
      <div className="border border-emerald-900 bg-emerald-950/20 p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">Meta Commerce catalog feed</h3>
        <p className="text-sm text-zinc-300">
          Your product catalog is already live as an XML feed that Meta can ingest. Use this URL when setting up your Catalog in Commerce Manager:
        </p>
        <div className="border border-[#333] bg-black p-3 font-mono text-xs text-emerald-300 break-all">
          https://torontocustomtshirts.ca/api/feeds/meta-catalog.xml
        </div>
        <p className="text-xs text-zinc-500">
          Feed updates automatically every time you edit a Ready-Made listing — no manual re-uploads. Meta refetches it daily.
        </p>
      </div>

      {/* Setup instructions */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-4">
        <h3 className="font-display text-lg uppercase tracking-tight">Setup walkthrough (15 min)</h3>
        <ol className="list-decimal pl-5 space-y-3 text-sm text-zinc-300">
          <li>
            <strong>Get your Pixel ID:</strong> Go to <a href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">Meta Events Manager <ExternalLink size={12} /></a> → Data Sources → click your Pixel → the 15-16 digit ID is at the top. Paste it above and hit SAVE.
          </li>
          <li>
            <strong>Create the catalog:</strong> Go to <a href="https://business.facebook.com/commerce" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">Commerce Manager <ExternalLink size={12} /></a> → Get Started → Catalog → Apparel.
          </li>
          <li>
            <strong>Add data source:</strong> Data Sources → Add Items → &quot;Use data feeds&quot; → paste the feed URL shown above. Set refresh schedule to &quot;Daily&quot;.
          </li>
          <li>
            <strong>Connect to Instagram:</strong> Commerce Manager → Connect Accounts → link your Instagram business profile. Once Meta approves your catalog (1-3 days) you can tag products in Reels and posts.
          </li>
          <li>
            <strong>Verify the Pixel:</strong> Install the <a href="https://chromewebstore.google.com/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">Meta Pixel Helper Chrome extension <ExternalLink size={12} /></a>. Visit your site — you should see a green ✓ with PageView firing, then ViewContent on a product page.
          </li>
        </ol>
      </div>
    </div>
  );
}
